import { useEffect, useMemo, useState } from "react";
import { baseurl, baseurImage } from "../../../Baseurl";
import axiosInstance from "../../../api/axiosInstance";
import { toast, ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";

// ── Theme ────────────────────────────────────────────────
const C = {
  gray:   '#5B6B79',
  blue:   '#96C5F7',
  red:    '#9E2B25',
  orange: '#F29E4C',
  green:  '#2CA87F',
}

const TABS = [
  { id: 'all',    label: 'All',     icon: 'ti-layout-grid', color: C.gray   },
  { id: 'staff',  label: 'Staff',   icon: 'ti-id-badge',    color: C.blue   },
  { id: 'doctor', label: 'Doctors', icon: 'ti-stethoscope', color: C.green  },
  { id: 'nurse',  label: 'Nurses',  icon: 'ti-heart',       color: C.orange },
]

const thStyle = {
  fontSize: 11, fontWeight: 700, color: C.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 10px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
}
const tdStyle = {
  fontSize: 13, color: '#2d3748', padding: '7px 10px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
}

const UserAvatar = ({ user }) => (
  <img
    src={user?.personalPhoto && user.personalPhoto !== 'null' && user.personalPhoto !== 'undefined'
      ? `${baseurImage}${user.personalPhoto}`
      : '../assets/images/user/avatar-1.jpg'}
    alt="user"
    onError={e => { e.target.src = '../assets/images/user/avatar-1.jpg' }}
    style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${C.blue}` }}
  />
)

const GenderBadge = ({ gender }) => {
  const g = (gender || '').toLowerCase()
  const bg = g === 'male' ? '#d0e8ff' : g === 'female' ? '#fce4ec' : '#eee'
  const color = g === 'male' ? '#1565c0' : g === 'female' ? '#880e4f' : '#555'
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: bg, color }}>{gender || '—'}</span>
}

const RoleBadge = ({ role }) => {
  const r = (role || '').toLowerCase()
  const map = { admin: [C.red, '#fff'], doctor: [C.green, '#fff'], nurse: [C.orange, '#fff'], receptionist: [C.blue, '#fff'] }
  const [bg, color] = map[r] || [C.gray, '#fff']
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: bg, color, textTransform: 'capitalize' }}>{role || '—'}</span>
}

const SearchBox = ({ value, onChange, placeholder }) => (
  <div style={{ position: 'relative', maxWidth: 260 }}>
    <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.gray, fontSize: 14, pointerEvents: 'none' }} />
    <input type="text" value={value} onChange={onChange} placeholder={placeholder}
      style={{ paddingLeft: 32, paddingRight: 10, height: 34, fontSize: 13, border: '1px solid #dde3eb', borderRadius: 8, width: '100%', outline: 'none', color: C.gray }} />
  </div>
)

const paginBtn = (disabled) => ({
  width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.blue}`,
  background: disabled ? '#f5f7fa' : C.blue, color: disabled ? '#aaa' : '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13,
})

function StaffPagination({ currentPage, totalPages, setCurrentPage }) {
  if (!totalPages || totalPages <= 1) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} style={paginBtn(currentPage === 1)}><i className="ti ti-chevron-left" /></button>
      <span style={{ fontSize: 12, color: C.gray, fontWeight: 600 }}>{currentPage} / {totalPages}</span>
      <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} style={paginBtn(currentPage === totalPages)}><i className="ti ti-chevron-right" /></button>
    </div>
  )
}

function StaffTablePanel({ data, columns, search, addBtn, pagination, total, accentColor }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {search}
          {addBtn}
        </div>
        <span style={{ fontSize: 11, color: C.gray }}>{total} record{total !== 1 ? 's' : ''}</span>
      </div>
      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key} style={{ ...thStyle, borderLeft: col.key === 'photo' ? `3px solid ${accentColor}` : undefined }}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr><td colSpan={columns.length} style={{ ...tdStyle, textAlign: 'center', color: C.gray, padding: '32px 0' }}>
                <i className="ti ti-database-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />No records found
              </td></tr>
            ) : data.map((item, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}
                onMouseEnter={e => e.currentTarget.style.background = `${accentColor}0d`}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}>
                {columns.map(col => <td key={col.key} style={tdStyle}>{col.render(item)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>{pagination}</div>
    </div>
  )
}

const IBtn = ({ icon, title, color, onClick }) => (
  <button onClick={onClick} title={title} style={{
    width: 28, height: 28, borderRadius: 7, border: 'none', cursor: 'pointer',
    background: `${color}22`, color, display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 14, marginRight: 3,
  }}
    onMouseEnter={e => e.currentTarget.style.background = color + '44'}
    onMouseLeave={e => e.currentTarget.style.background = color + '22'}>
    <i className={`ti ${icon}`} />
  </button>
)
// ─────────────────────────────────────────────────────────

export default function Staff() {
  const [activeTab, setActiveTab] = useState('all');
  const [openmodal, setOpenmodal] = useState(false);
  const [openmodal2, setOpenmodal2] = useState(false);
  const [openmodal111, setOpenmodal111] = useState(false);


  const [allDoctors, setAllDoctors] = useState([]);
  const [allNurses, getAllNurses] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [allUser, setAllUser] = useState([]);
  const [currentPageAll, setCurrentPageAll] = useState(1);
  const [currentPageStaff, setCurrentPageStaff] = useState(1);
  const [currentPageDoctor, setCurrentPageDoctor] = useState(1);
  const [currentPageNurse, setCurrentPageNurse] = useState(1);
  const itemsPerPage = 10;
  const [searchAll, setSearchAll] = useState("");
  const [searchStaff, setSearchStaff] = useState("");
  const [searchDoctor, setSearchDoctor] = useState("");
  const [searchNurse, setSearchNurse] = useState("");
  const [dataDoctor, setDataDoctor] = useState({
    email: "",
    password: "",
    fullName: "",
    shortName: "",
    prefix: "",
    dateOfBirth: "",
    licenseId: "",
    civilId: "",
    passport: "",
    gender: "",
    specialty: "",
    phoneNumber: "",
  });
  const [files1, setFiles1] = useState(null);
  const [specialities, setSpecialities] = useState([]);
  const [loadingSpecialities, setLoadingSpecialities] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isSecurityMode = useMemo(() => location.pathname?.toLowerCase().includes('/admin/security'), [location.pathname]);

  // Security mode: permission modal state
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const modulesList = useMemo(() => (
    [
      { key: 'dashboard', label: 'Dashboard' },
      { key: 'appointments', label: 'Appointments' },
      { key: 'labs', label: 'Labs' },
      { key: 'billing', label: 'Billing' },
      { key: 'pharmacy', label: 'Pharmacy' },
      { key: 'medicals', label: 'Medicals' },
      { key: 'services', label: 'Services' },
      { key: 'reports', label: 'Reports' },
      { key: 'insurance', label: 'Insurance' },
      { key: 'patients', label: 'Patients' },
      { key: 'files', label: 'Files' },
      { key: 'settings', label: 'Settings' },
    ]
  ), []);
  const [permissions, setPermissions] = useState({});
  const initPermissions = () => {
    const next = {};
    modulesList.forEach(m => {
      next[m.key] = { view: false, edit: false, delete: false };
    });
    setPermissions(next);
  };

  // Helper function to get user image URL
  const getUserImageUrl = (user) => {
    if (user.personalPhoto && user.personalPhoto !== "null" && user.personalPhoto !== "undefined") {
      return `${baseurImage}${user.personalPhoto}`;
    }
    // Return default avatar if no personal photo
    return "../assets/images/user/avatar-1.jpg";
  };
  const handleclick = () => {
    setOpenmodal(true);
  };
  const handleclicknurse = () => {
    setOpenmodal111(true);
  };
  const handleclicknurse123 = () => {
    setOpenmodal111(false);
  };
  const handleclick123 = () => {
    setOpenmodal2(true);
  };
  const handleClose = () => {
    setOpenmodal(false);
    // Reset form
    setDataDoctor({
      email: "",
      password: "",
      fullName: "",
      shortName: "",
      prefix: "",
      dateOfBirth: "",
      licenseId: "",
      civilId: "",
      passport: "",
      gender: "",
      specialty: "",
      phoneNumber: "",
    });
    setFiles1(null);
  };
  const handleClose2 = () => {
    setOpenmodal2(false);
  };

  const handleEditClick = (doctor) => {
    navigate("/Admin/EditDoctor", { state: { doctor } });
  };



  const handlefilechnage = (e) => {
    const file = e.target.files[0];
    setFiles1(file);
  };

  const AddNurse = async () => {
    try {
      if (!dataDoctor.email || !dataDoctor.fullName || !files1) {
        Swal.fire("Missing Fields", "Please fill all required fields", "warning");
        return;
      }
      const formData = new FormData();
      formData.append("email", dataDoctor.email);
      formData.append("password", dataDoctor.password);
      formData.append("role", "nurse");
      formData.append("fullName", dataDoctor.fullName);
      formData.append("shortName", dataDoctor.shortName);
      formData.append("prefix", dataDoctor.prefix);
      formData.append("dateOfBirth", dataDoctor.dateOfBirth);
      formData.append("licenseId", dataDoctor.licenseId);
      formData.append("civilId", dataDoctor.civilId);
      formData.append("phoneNumber", dataDoctor.phoneNumber);
      formData.append("passport", dataDoctor.passport);
      formData.append("gender", dataDoctor.gender);
      formData.append("personalPhoto", files1);

      const response = await axiosInstance.post(
        `addDoctorDetails`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.success === true) {
        Swal.fire("Success", "Nurse added successfully", "success");
        getallNurse()
        handleclicknurse123();
        setOpenmodal(false);
      } else {
        const msg = response.data.message || "Something went wrong!";
        Swal.fire("Error", msg, "error");
        console.error(" Server Error:", response.data);
      }
    } catch (error) {
      console.error("Error while adding nurse:", error);
      if (error.response) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          Swal.fire("Server Error", errorData, "error");
        } else if (errorData.message) {
          Swal.fire("Error", errorData.message, "error");
        } else if (Array.isArray(errorData.errors)) {
          const allErrors = errorData.errors.map(err => `• ${err.msg || err.message}`).join('<br>');
          Swal.fire({
            title: "Validation Errors",
            html: allErrors,
            icon: "error"
          });
        } else {
          Swal.fire("Error", "Unexpected server error occurred", "error");
        }
      } else if (error.request) {
        Swal.fire("Network Error", "No response from server. Please check your connection.", "error");
      } else {
        Swal.fire("Error", error.message || "An unknown error occurred", "error");
      }
    }
  };



  const AddDocotor = async () => {
    try {
      if (!dataDoctor.email || !dataDoctor.fullName || !files1) {
        Swal.fire("Missing Fields", "Please fill all required fields", "warning");
        return;
      }
      const formData = new FormData();
      formData.append("email", dataDoctor.email);
      formData.append("password", dataDoctor.password);
      formData.append("role", dataDoctor.role || "doctor");
      formData.append("fullName", dataDoctor.fullName);
      formData.append("shortName", dataDoctor.shortName);
      formData.append("prefix", dataDoctor.prefix);
      formData.append("dateOfBirth", dataDoctor.dateOfBirth);
      formData.append("licenseId", dataDoctor.licenseId);
      formData.append("civilId", dataDoctor.civilId);
      formData.append("passport", dataDoctor.passport);
      formData.append("gender", dataDoctor.gender);
      formData.append("specialty", dataDoctor.specialty);
      formData.append("phoneNumber", dataDoctor.phoneNumber);
      formData.append("personalPhoto", files1);

      const response = await axiosInstance.post(
        `addDoctorDetails`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.success === true) {
        Swal.fire("Success", response.data.message || "Doctor added successfully!", "success");

        // Reset form
        setDataDoctor({
          email: "",
          password: "",
          fullName: "",
          shortName: "",
          prefix: "",
          dateOfBirth: "",
          licenseId: "",
          civilId: "",
          passport: "",
          gender: "",
          specialty: "",
          phoneNumber: "",
        });
        setFiles1(null);
        setOpenmodal(false);
        getalldoctor(); // Refresh the list
      } else {
        const msg = response.data.message || "Something went wrong";
        Swal.fire("Error", msg, "error");
        console.error("Server Error:", msg);
      }
    } catch (error) {
      console.error("Error while adding doctor:", error);
      if (error.response) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          Swal.fire("Server Error", errorData, "error");
        } else if (errorData.message) {
          Swal.fire("Error", errorData.message, "error");
        } else if (Array.isArray(errorData.errors)) {
          for (const err of errorData.errors) {
            Swal.fire("Validation Error", err.msg || err.message, "error");
          }
        } else {
          Swal.fire("Error", "Unexpected server error occurred", "error");
        }
      } else if (error.request) {
        Swal.fire("Network Error", "No response from server. Please check your connection.", "error");
      } else {
        Swal.fire("Error", error.message || "An unknown error occurred", "error");
      }
    }
  };

  const handlechaneg = (e) => {
    const { name, value } = e.target;
    setDataDoctor({ ...dataDoctor, [name]: value });
  };
  useEffect(() => {
    getalldoctor();
  }, []);
  const getalldoctor = async () => {
    try {
      const response = await axiosInstance.get(`getAllDoctors`);
      if (response.data.success === true) {
        setAllDoctors(response.data.data);
      } else {
        console.error("get all doctors error");
      }
    } catch (error) {
      console.error(error.response.data);
    }
  };
  useEffect(() => {
    getallNurse();
  }, []);
  const getallNurse = async () => {
    try {
      const response = await axiosInstance.get(`getAllNurses`);
      if (response.data.success === true) {

        getAllNurses(response.data.data);
      } else {
        console.error("get all nurses error");
      }
    } catch (error) {
      console.err(error.response.data);
    }
  };
  useEffect(() => {
    getallstaff();
  }, []);
  const getallstaff = async () => {
    try {
      const response = await axiosInstance.get(`getAllStaff`);
      if (response.data.success === true) {
        setAllStaff(response.data.data);
      } else {
        console.error("get all staff error");
      }
    } catch (error) {
      console.error(error.response.data);
    }
  };
  useEffect(() => {
    getalluser();
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoadingSpecialities(true);
      const response = await axiosInstance.get(`getAllServiceCategories`);
      if (response.data.success === true) {
        setSpecialities(response.data.data); // still reuse specialities state
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingSpecialities(false);
    }
  };


  const getalluser = async () => {
    try {
      const response = await axiosInstance.get(`getAllUsers`);
      if (response.data.success === true) {
        setAllUser(response.data.data);
      } else {
        console.error("get all users error");
      }
    } catch (error) {
      console.error(error.response.data);
    }
  };

  const handlenavigate = (id) => {
    navigate("/Admin/DoctorBooking", { state: { data: id } });
  };

  const handlefilterdata = () => {
    navigate("/Admin/DoctorDashboard");
  };

  const cardCount = 6;

  const handleStatusChange = async (doctorId, newStatus) => {
    // Example logic (replace with actual API or state update)
    setAllDoctors((prevDoctors) =>
      prevDoctors.map((doc) =>
        doc.id === doctorId ? { ...doc, status: newStatus } : doc
      )
    );

    // Optional: Call backend API to save status
    try {
      const response = await axiosInstance.post(
        `changeDoctorStatus/${doctorId}`,
        { status: parseInt(newStatus) }
      );
      if (response.data.success === true) {
        getalldoctor();
        Swal.fire("success", "Status Changed Successfully", "success");
      } else {
        Swal.fire("error", "something went wrong", "error");
      }
    } catch (error) {
      const err = error.response.data.message;
      Swal.fire("error", err, "error");
    }
  };

  const handleclickstaff = async () => {
    try {
      const formData = new FormData();
      formData.append("email", dataDoctor.email);
      formData.append("password", dataDoctor.password);
      formData.append("role", dataDoctor.role);
      formData.append("fullName", dataDoctor.firstName);
      formData.append("shortName", dataDoctor.lastName);
      formData.append("prefix", dataDoctor.prefix);
      formData.append("dateOfBirth", dataDoctor.dateOfBirth);
      formData.append("licenseId", dataDoctor.licenseId);
      formData.append("civilId", dataDoctor.civilId);
      formData.append("passport", dataDoctor.passport);
      formData.append("gender", dataDoctor.gender);
      formData.append("phoneNumber", dataDoctor.phoneNumber);
      formData.append("personalPhoto", files1);

      const response = await axiosInstance.post(
        `addDoctorDetails`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success === true) {
        Swal.fire("Success", "Staff added successfully", "success");
        getallstaff()
        handleClose2();
      } else {
        const errorMessage = response.data.message || "Something went wrong!";
        Swal.fire("Error", errorMessage, "error");
        console.error("Server responded with an error:", response.data);
      }
    } catch (error) {
      console.error("Error while adding doctor:", error);

      if (error.response) {
        const errorData = error.response.data;

        if (typeof errorData === "string") {
          Swal.fire("Error", errorData, "error");
        } else if (errorData.message) {
          Swal.fire("Error", errorData.message, "error");
        } else if (Array.isArray(errorData.errors)) {
          // Show all errors in one alert
          const allErrors = errorData.errors.map(err => `• ${err.msg || err.message}`).join('<br>');
          Swal.fire({
            title: "Validation Errors",
            html: allErrors,
            icon: "error"
          });
        } else {
          Swal.fire("Error", "Unexpected server error occurred", "error");
        }
      } else if (error.request) {
        Swal.fire("Network Error", "No response from server. Please check your internet connection.", "error");
      } else {
        Swal.fire("Error", error.message || "An unknown error occurred", "error");
      }
    }
  };

  const handleStaffEditClick = (staff) => {
    navigate("/Admin/EditStaff", { state: { staff } });
  };



  const handleViewUserClick = (user) => {
    if (isSecurityMode) {
      setSelectedAccount(user);
      initPermissions();
      setPermissionModalOpen(true);
      return;
    }
    navigate(`/Admin/ViewUser`, { state: { user } });
  };

  const handleViewStaffClick = (staff) => {
    if (isSecurityMode) {
      setSelectedAccount(staff);
      initPermissions();
      setPermissionModalOpen(true);
      return;
    }
    navigate(`/Admin/ViewStaff`, { state: { staff } });
  };

  const handleEditNurseClick = (nurse) => {
    navigate("/Admin/EditNurse", { state: { nurse } });
  };



  const handleViewNurseClick = (nurse) => {
    if (isSecurityMode) {
      setSelectedAccount(nurse);
      initPermissions();
      setPermissionModalOpen(true);
      return;
    }
    navigate(`/Admin/ViewNurse`, { state: { nurse } });
  };



  const handleViewDoctorClick = (doctor) => {
    if (isSecurityMode) {
      setSelectedAccount(doctor);
      initPermissions();
      setPermissionModalOpen(true);
      return;
    }
    navigate(`/Admin/ViewDoctor`, { state: { doctor } });
  };

  // Permission toggle handlers
  const togglePermission = (moduleKey, permKey) => {
    setPermissions(prev => ({
      ...prev,
      [moduleKey]: { ...prev[moduleKey], [permKey]: !prev[moduleKey]?.[permKey] }
    }));
  };

  const closePermissionModal = () => {
    setPermissionModalOpen(false);
    setSelectedAccount(null);
  };

  const handleSavePermissions = () => {
    // UI-only for now
    Swal.fire('Saved', 'Permissions updated (UI only)', 'success');
    setPermissionModalOpen(false);
  };





  // Delete functions for all user types
  const handleDeleteUser = async (userId) => {

    if (!userId || userId === 'undefined') {
      Swal.fire("Error", "Invalid user ID. Please try again.", "error");
      return;
    }

    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!"
      });

      if (result.isConfirmed) {
        const response = await axiosInstance.post(`softDeleteUser/${userId}`);

        if (response.data.success) {
          Swal.fire("Deleted!", "User has been deleted.", "success");
          getalluser(); // Refresh the list
        } else {
          Swal.fire("Error", response.data.message || "Failed to delete user", "error");
        }
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      Swal.fire("Error", "Failed to delete user", "error");
    }
  };

  const handleDeleteStaff = async (staffId) => {

    if (!staffId || staffId === 'undefined') {
      Swal.fire("Error", "Invalid staff ID. Please try again.", "error");
      return;
    }

    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!"
      });

      if (result.isConfirmed) {
        const response = await axiosInstance.post(`softDeleteUser/${staffId}`);

        if (response.data.success) {
          Swal.fire("Deleted!", "Staff has been deleted.", "success");
          getallstaff(); // Refresh the list
        } else {
          Swal.fire("Error", response.data.message || "Failed to delete staff", "error");
        }
      }
    } catch (error) {
      console.error("Error deleting staff:", error);
      Swal.fire("Error", "Failed to delete staff", "error");
    }
  };

  const handleDeleteDoctor = async (doctorId) => {
    if (!doctorId || doctorId === 'undefined') {
      Swal.fire("Error", "Invalid doctor ID. Please try again.", "error");
      return;
    }

    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!"
      });

      if (result.isConfirmed) {
        const response = await axiosInstance.post(`softDeleteUser/${doctorId}`);

        if (response.data.success) {
          Swal.fire("Deleted!", "Doctor has been deleted.", "success");
          getalldoctor(); // Refresh the list
        } else {
          Swal.fire("Error", response.data.message || "Failed to delete doctor", "error");
        }
      }
    } catch (error) {
      console.error("Error deleting doctor:", error);
      Swal.fire("Error", "Failed to delete doctor", "error");
    }
  };

  const handleDeleteNurse = async (id) => {

    if (!id || id === 'undefined') {
      Swal.fire("Error", "Invalid nurse ID. Please try again.", "error");
      return;
    }

    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!"
      });

      if (result.isConfirmed) {
        const response = await axiosInstance.post(`softDeleteUser/${id}`);

        if (response.data.success) {
          Swal.fire("Deleted!", "Nurse has been deleted.", "success");
          getAllNurses(); // Refresh the list
        } else {
          Swal.fire("Error", response.data.message || "Failed to delete nurse", "error");
        }
      }
    } catch (error) {
      console.error("❌ Error deleting nurse:", error);
      Swal.fire("Error", "Failed to delete nurse", "error");
    }
  };

  // Unified delete function for All tab
  const handleDeleteByRole = async (item) => {
    const itemId = item.id || item._id;

    if (!itemId || itemId === 'undefined') {
      Swal.fire("Error", "Invalid ID. Please try again.", "error");
      return;
    }

    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!"
      });

      if (result.isConfirmed) {
        const response = await axiosInstance.delete(`deleteDoctor/${itemId}`);

        if (response.data.success) {
          Swal.fire("Deleted!", `${item.role || 'User'} has been deleted.`, "success");
          // Refresh all lists
          getalluser();
          getallstaff();
          getalldoctor();
          getAllNurses();
        } else {
          Swal.fire("Error", response.data.message || "Failed to delete", "error");
        }
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      Swal.fire("Error", "Failed to delete", "error");
    }
  };

  const [viewAppointmentModal, setViewAppointmentModal] = useState(false);
  const [viewAppointmentDoctor, setViewAppointmentDoctor] = useState(null);
  const [appointmentData, setAppointmentData] = useState([]);

  const handleViewAppointmentClick = async (doctor) => {
    setViewAppointmentDoctor(doctor);
    setViewAppointmentModal(true);

    try {
      const response = await axiosInstance.get(
        `getAppointmentsByDoctorId/${doctor.id}`
      );
      if (response.data.success === true) {
        setAppointmentData(response.data.data);
      } else {
        console.error("Failed to fetch appointments");
        setAppointmentData([]);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      setAppointmentData([]);
    }
  };

  const handleViewAppointmentClose = () => {
    setViewAppointmentModal(false);
    setViewAppointmentDoctor(null);
    setAppointmentData([]);
  };

  // Search filters
  const filteredAllUser = (allUser || []).filter((item) => {
    const q = searchAll.toLowerCase();
    return (
      (item.fullName && item.fullName.toLowerCase().includes(q)) ||
      (item.email && item.email.toLowerCase().includes(q)) ||
      (item.civilId && String(item.civilId).toLowerCase().includes(q)) ||
      (item.phoneNumber && String(item.phoneNumber).toLowerCase().includes(q)) ||
      (item.role && item.role.toLowerCase().includes(q))
    );
  });

  const filteredStaff = (allStaff || []).filter((item) => {
    const q = searchStaff.toLowerCase();
    return (
      (item.fullName && item.fullName.toLowerCase().includes(q)) ||
      (item.email && item.email.toLowerCase().includes(q)) ||
      (item.civilId && String(item.civilId).toLowerCase().includes(q)) ||
      (item.phoneNumber && String(item.phoneNumber).toLowerCase().includes(q)) ||
      (item.licenseId && String(item.licenseId).toLowerCase().includes(q))
    );
  });

  const filteredDoctors = (allDoctors || []).filter((item) => {
    const q = searchDoctor.toLowerCase();
    return (
      (item.fullName && item.fullName.toLowerCase().includes(q)) ||
      (item.email && item.email.toLowerCase().includes(q)) ||
      (item.specialty && item.specialty.toLowerCase().includes(q)) ||
      (item.civilId && String(item.civilId).toLowerCase().includes(q)) ||
      (item.phoneNumber && String(item.phoneNumber).toLowerCase().includes(q)) ||
      (item.licenseId && String(item.licenseId).toLowerCase().includes(q))
    );
  });

  const filteredNurses = (allNurses || []).filter((item) => {
    const q = searchNurse.toLowerCase();
    return (
      (item.fullName && item.fullName.toLowerCase().includes(q)) ||
      (item.email && item.email.toLowerCase().includes(q)) ||
      (item.civilId && String(item.civilId).toLowerCase().includes(q)) ||
      (item.phoneNumber && String(item.phoneNumber).toLowerCase().includes(q))
    );
  });

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => navigate(-1)} title="Go back" style={{
              width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
              background: '#f8f9fb', color: C.gray, cursor: 'pointer', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, marginRight: 4,
            }}
              onMouseEnter={e => { e.currentTarget.style.background = C.blue + '22'; e.currentTarget.style.color = C.blue }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = C.gray }}>
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${C.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-users" style={{ color: C.blue, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Users</div>
              <div style={{ fontSize: 11, color: C.gray }}>Manage staff, doctors and nurses</div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, padding: '10px 20px 0', borderBottom: '1px solid #e8ecf0', background: '#fafbfc' }}>
            {TABS.map(tab => {
              const active = activeTab === tab.id
              const count = tab.id === 'all' ? allUser.length : tab.id === 'staff' ? allStaff.length : tab.id === 'doctor' ? allDoctors.length : allNurses.length
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px',
                  fontSize: 13, fontWeight: active ? 700 : 500,
                  color: active ? tab.color : C.gray,
                  background: active ? '#fff' : 'transparent',
                  border: '1px solid', borderColor: active ? '#e8ecf0' : 'transparent',
                  borderBottom: active ? '2px solid ' + tab.color : '2px solid transparent',
                  borderRadius: '8px 8px 0 0', cursor: 'pointer', marginBottom: -1, transition: 'all 0.15s',
                }}>
                  <i className={`ti ${tab.icon}`} style={{ fontSize: 15 }} />
                  {tab.label}
                  <span style={{
                    fontSize: 10, fontWeight: 700, minWidth: 18, height: 18, borderRadius: 9,
                    background: active ? tab.color : '#e8ecf0', color: active ? '#fff' : C.gray,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
                  }}>{count}</span>
                </button>
              )
            })}
          </div>

          {/* Tab Panels */}
          <div style={{ padding: '14px 20px' }}>

            {/* ALL */}
            {activeTab === 'all' && (
              <StaffTablePanel
                data={filteredAllUser.slice((currentPageAll - 1) * itemsPerPage, currentPageAll * itemsPerPage)}
                columns={[
                  { label: '', key: 'photo', render: item => <UserAvatar user={item} /> },
                  { label: 'Name', key: 'name', render: item => (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{item.displayName || item.fullName || '—'}</div>
                      <div style={{ fontSize: 11, color: C.gray }}>{item.email || ''}</div>
                    </div>
                  )},
                  { label: 'Civil ID', key: 'civil', render: item => <span style={{ fontSize: 12 }}>{item.civilId || '—'}</span> },
                  { label: 'DOB', key: 'dob', render: item => <span style={{ fontSize: 12 }}>{item.dateOfBirth ? new Date(item.dateOfBirth).toLocaleDateString('en-GB') : '—'}</span> },
                  { label: 'Phone', key: 'phone', render: item => <span style={{ fontSize: 12 }}>{item.phoneNumber || '—'}</span> },
                  { label: 'Role', key: 'role', render: item => <RoleBadge role={item.role} /> },
                  { label: '', key: 'action', render: item => (
                    <>
                      <IBtn icon="ti-eye" title="View User" color={C.blue} onClick={() => handleViewUserClick(item)} />
                      <IBtn icon="ti-trash" title="Delete User" color={C.red} onClick={() => handleDeleteByRole(item)} />
                    </>
                  )},
                ]}
                search={<SearchBox value={searchAll} onChange={e => { setSearchAll(e.target.value); setCurrentPageAll(1) }} placeholder="Search users..." />}
                pagination={<StaffPagination currentPage={currentPageAll} totalPages={Math.ceil(filteredAllUser.length / itemsPerPage) || 1} setCurrentPage={setCurrentPageAll} />}
                total={filteredAllUser.length}
                accentColor={C.gray}
              />
            )}

            {/* STAFF */}
            {activeTab === 'staff' && (
              <StaffTablePanel
                data={filteredStaff.slice((currentPageStaff - 1) * itemsPerPage, currentPageStaff * itemsPerPage)}
                columns={[
                  { label: '', key: 'photo', render: item => <UserAvatar user={item} /> },
                  { label: 'Name', key: 'name', render: item => (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{item.displayName || item.fullName || '—'}</div>
                      <div style={{ fontSize: 11, color: C.gray }}>{item.email || ''}</div>
                    </div>
                  )},
                  { label: 'DOB', key: 'dob', render: item => <span style={{ fontSize: 12 }}>{item.dateOfBirth ? new Date(item.dateOfBirth).toLocaleDateString('en-GB') : '—'}</span> },
                  { label: 'License', key: 'lic', render: item => <span style={{ fontSize: 12 }}>{item.licenseId || '—'}</span> },
                  { label: 'Civil ID', key: 'civil', render: item => <span style={{ fontSize: 12 }}>{item.civilId || '—'}</span> },
                  { label: 'Phone', key: 'phone', render: item => <span style={{ fontSize: 12 }}>{item.phoneNumber || '—'}</span> },
                  { label: 'Gender', key: 'gender', render: item => <GenderBadge gender={item.gender} /> },
                  { label: '', key: 'action', render: item => (
                    <>
                      <IBtn icon="ti-eye" title="View Staff" color={C.blue} onClick={() => handleViewStaffClick(item)} />
                      <IBtn icon="ti-edit" title="Edit Staff" color={C.orange} onClick={() => handleStaffEditClick(item)} />
                      <IBtn icon="ti-trash" title="Delete Staff" color={C.red} onClick={() => handleDeleteStaff(item.id || item._id || item.staffId)} />
                    </>
                  )},
                ]}
                search={<SearchBox value={searchStaff} onChange={e => { setSearchStaff(e.target.value); setCurrentPageStaff(1) }} placeholder="Search staff..." />}
                addBtn={<button className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: '5px 12px' }} onClick={() => navigate('/Admin/AddStaff')}><i className="ti ti-plus" style={{ fontSize: 14 }} /> Add Staff</button>}
                pagination={<StaffPagination currentPage={currentPageStaff} totalPages={Math.ceil(filteredStaff.length / itemsPerPage) || 1} setCurrentPage={setCurrentPageStaff} />}
                total={filteredStaff.length}
                accentColor={C.blue}
              />
            )}

            {/* DOCTORS */}
            {activeTab === 'doctor' && (
              <StaffTablePanel
                data={filteredDoctors.slice((currentPageDoctor - 1) * itemsPerPage, currentPageDoctor * itemsPerPage)}
                columns={[
                  { label: '', key: 'photo', render: item => <UserAvatar user={item} /> },
                  { label: 'Name', key: 'name', render: item => (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{item.displayName || item.fullName || '—'}</div>
                      <div style={{ fontSize: 11, color: C.gray }}>{item.email || ''}</div>
                    </div>
                  )},
                  { label: 'DOB', key: 'dob', render: item => <span style={{ fontSize: 12 }}>{item.dateOfBirth ? new Date(item.dateOfBirth).toLocaleDateString('en-GB') : '—'}</span> },
                  { label: 'License', key: 'lic', render: item => <span style={{ fontSize: 12 }}>{item.licenseId || '—'}</span> },
                  { label: 'Gender', key: 'gender', render: item => <GenderBadge gender={item.gender} /> },
                  { label: 'Specialty', key: 'spec', render: item => item.specialty
                    ? <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: `${C.green}18`, color: C.green }}>{item.specialty}</span>
                    : <span style={{ color: '#aaa' }}>—</span>
                  },
                  { label: 'Status', key: 'status', render: item => (
                    <div className="form-check form-switch mb-0">
                      <input className="form-check-input" type="checkbox" role="switch" id={`toggle-${item.id}`}
                        checked={Number(item.is_active) === 1}
                        onChange={e => handleStatusChange(item.id, e.target.checked ? 1 : 0)} />
                      <label className="form-check-label ms-1" htmlFor={`toggle-${item.id}`} style={{ fontSize: 12, color: C.gray }}>
                        {Number(item.is_active) === 1 ? 'Active' : 'Inactive'}
                      </label>
                    </div>
                  )},
                  { label: '', key: 'action', render: item => (
                    <>
                      <IBtn icon="ti-calendar" title="View Appointments" color={C.blue} onClick={() => handleViewAppointmentClick(item)} />
                      <IBtn icon="ti-eye" title="View Doctor" color={C.green} onClick={() => handleViewDoctorClick(item)} />
                      <IBtn icon="ti-edit" title="Edit Doctor" color={C.orange} onClick={() => handleEditClick(item)} />
                      <IBtn icon="ti-trash" title="Delete Doctor" color={C.red} onClick={() => handleDeleteDoctor(item.id || item._id || item.doctorId)} />
                    </>
                  )},
                ]}
                search={<SearchBox value={searchDoctor} onChange={e => { setSearchDoctor(e.target.value); setCurrentPageDoctor(1) }} placeholder="Search doctors..." />}
                addBtn={<button className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: '5px 12px' }} onClick={() => navigate('/Admin/AddDoctor')}><i className="ti ti-plus" style={{ fontSize: 14 }} /> Add Doctor</button>}
                pagination={<StaffPagination currentPage={currentPageDoctor} totalPages={Math.ceil(filteredDoctors.length / itemsPerPage) || 1} setCurrentPage={setCurrentPageDoctor} />}
                total={filteredDoctors.length}
                accentColor={C.green}
              />
            )}

            {/* NURSES */}
            {activeTab === 'nurse' && (
              <StaffTablePanel
                data={filteredNurses.slice((currentPageNurse - 1) * itemsPerPage, currentPageNurse * itemsPerPage)}
                columns={[
                  { label: '', key: 'photo', render: item => <UserAvatar user={item} /> },
                  { label: 'Name', key: 'name', render: item => (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{item.displayName || item.fullName || '—'}</div>
                      <div style={{ fontSize: 11, color: C.gray }}>{item.email || ''}</div>
                    </div>
                  )},
                  { label: 'DOB', key: 'dob', render: item => <span style={{ fontSize: 12 }}>{item.dateOfBirth ? new Date(item.dateOfBirth).toLocaleDateString('en-GB') : '—'}</span> },
                  { label: 'Civil ID', key: 'civil', render: item => <span style={{ fontSize: 12 }}>{item.civilId || '—'}</span> },
                  { label: 'Phone', key: 'phone', render: item => <span style={{ fontSize: 12 }}>{item.phoneNumber || '—'}</span> },
                  { label: 'Gender', key: 'gender', render: item => <GenderBadge gender={item.gender} /> },
                  { label: '', key: 'action', render: item => (
                    <>
                      <IBtn icon="ti-eye" title="View Nurse" color={C.blue} onClick={() => handleViewNurseClick(item)} />
                      <IBtn icon="ti-edit" title="Edit Nurse" color={C.orange} onClick={() => handleEditNurseClick(item)} />
                      <IBtn icon="ti-trash" title="Delete Nurse" color={C.red} onClick={() => handleDeleteNurse(item.id || item._id || item.nurseId)} />
                    </>
                  )},
                ]}
                search={<SearchBox value={searchNurse} onChange={e => { setSearchNurse(e.target.value); setCurrentPageNurse(1) }} placeholder="Search nurses..." />}
                addBtn={<button className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: '5px 12px' }} onClick={() => navigate('/Admin/AddNurse')}><i className="ti ti-plus" style={{ fontSize: 14 }} /> Add Nurse</button>}
                pagination={<StaffPagination currentPage={currentPageNurse} totalPages={Math.ceil(filteredNurses.length / itemsPerPage) || 1} setCurrentPage={setCurrentPageNurse} />}
                total={filteredNurses.length}
                accentColor={C.orange}
              />
            )}

          </div>
        </div>
      </div>

      {/* ── Modals ───────────────────────────────────────────────────── */}

      {/* Add Staff Modal */}
      {openmodal2 && (
        <div className="modal fade show" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'block' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header" style={{ background: C.blue, color: '#fff', padding: '10px 16px' }}>
                <h5 className="modal-title">Add Staff</h5>
                <button type="button" className="btn-close btn-close-white" onClick={handleClose2}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Prefix</label>
                    <select className="form-select" name="prefix" onChange={handlechaneg}>
                      <option value="">Select</option>
                      <option value="Dr.">Dr.</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Miss">Miss</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">First Name</label>
                    <input type="text" className="form-control" name="firstName" placeholder="First name" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Last Name</label>
                    <input type="text" className="form-control" name="lastName" placeholder="Last name" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Civil ID</label>
                    <input type="text" className="form-control" name="civilId" placeholder="Civil ID" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Passport</label>
                    <input type="text" className="form-control" name="passport" placeholder="Passport number" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Date of Birth</label>
                    <input type="date" className="form-control" name="dateOfBirth" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Gender</label>
                    <select className="form-select" name="gender" onChange={handlechaneg}>
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" name="email" placeholder="email@example.com" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Phone Number</label>
                    <input type="text" className="form-control" name="phoneNumber" placeholder="Phone number" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">License ID</label>
                    <input type="text" className="form-control" name="licenseId" placeholder="License ID" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Role</label>
                    <select className="form-select" name="role" onChange={handlechaneg}>
                      <option value="">Select</option>
                      <option value="admin">Admin</option>
                      <option value="receptionist">Receptionist</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Password</label>
                    <input type="password" className="form-control" name="password" placeholder="Password" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Photo</label>
                    <input type="file" className="form-control" name="personalPhoto" onChange={handlefilechnage} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleClose2}>Cancel</button>
                <button type="submit" className="bgBtn btn-sm" style={{ padding: '7px 16px' }} onClick={handleclickstaff}>Add Staff</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Doctor Modal */}
      {openmodal && (
        <div className="modal fade show" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'block' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header" style={{ background: C.green, color: '#fff', padding: '10px 16px' }}>
                <h5 className="modal-title">Add Doctor</h5>
                <button type="button" className="btn-close btn-close-white" onClick={handleClose}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Prefix</label>
                    <select className="form-select" name="prefix" onChange={handlechaneg}>
                      <option value="">Select</option>
                      <option value="Dr.">Dr.</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Mrs.">Mrs.</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">First Name</label>
                    <input type="text" className="form-control" name="fullName" placeholder="First name" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Last Name</label>
                    <input type="text" className="form-control" name="shortName" placeholder="Last name" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" name="email" placeholder="doctor@email.com" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Gender</label>
                    <select className="form-select" name="gender" onChange={handlechaneg}>
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Password</label>
                    <input type="password" className="form-control" name="password" placeholder="Password" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Date of Birth</label>
                    <input type="date" className="form-control" name="dateOfBirth" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">License ID</label>
                    <input type="text" className="form-control" name="licenseId" placeholder="License number" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Civil ID</label>
                    <input type="text" className="form-control" name="civilId" placeholder="Civil ID" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Passport</label>
                    <input type="text" className="form-control" name="passport" placeholder="Passport number" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Specialty</label>
                    <select className="form-select" name="specialty" value={dataDoctor.specialty || ''} onChange={handlechaneg}>
                      <option value="">Select Specialty</option>
                      {loadingSpecialities ? <option disabled>Loading...</option> : specialities.map(c => <option key={c.id} value={c.category_name}>{c.category_name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Phone Number</label>
                    <input type="text" className="form-control" name="phoneNumber" placeholder="Phone number" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Photo</label>
                    <input type="file" className="form-control" name="personalPhoto" onChange={handlefilechnage} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleClose}>Cancel</button>
                <button type="submit" className="bgBtn btn-sm" style={{ padding: '7px 16px' }} onClick={AddDocotor}>Add Doctor</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Nurse Modal */}
      {openmodal111 && (
        <div className="modal fade show" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'block' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header" style={{ background: C.orange, color: '#fff', padding: '10px 16px' }}>
                <h5 className="modal-title">Add Nurse</h5>
                <button type="button" className="btn-close btn-close-white" onClick={handleclicknurse123}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Prefix</label>
                    <select className="form-select" name="prefix" onChange={handlechaneg}>
                      <option value="">Select</option>
                      <option value="Dr.">Dr.</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Miss">Miss</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">First Name</label>
                    <input type="text" className="form-control" name="fullName" placeholder="First name" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Last Name</label>
                    <input type="text" className="form-control" name="shortName" placeholder="Last name" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" name="email" placeholder="nurse@email.com" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Gender</label>
                    <select className="form-select" name="gender" onChange={handlechaneg}>
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Password</label>
                    <input type="password" className="form-control" name="password" placeholder="Password" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Date of Birth</label>
                    <input type="date" className="form-control" name="dateOfBirth" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">License ID</label>
                    <input type="text" className="form-control" name="licenseId" placeholder="License number" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Civil ID</label>
                    <input type="text" className="form-control" name="civilId" placeholder="Civil ID" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Passport</label>
                    <input type="text" className="form-control" name="passport" placeholder="Passport number" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Phone Number</label>
                    <input type="text" className="form-control" name="phoneNumber" placeholder="Phone number" onChange={handlechaneg} />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Photo</label>
                    <input type="file" className="form-control" name="personalPhoto" onChange={handlefilechnage} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleclicknurse123}>Cancel</button>
                <button type="submit" className="bgBtn btn-sm" style={{ padding: '7px 16px' }} onClick={AddNurse}>Add Nurse</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Appointment Modal */}
      {viewAppointmentModal && viewAppointmentDoctor && (
            <div
              className="modal fade show"
              aria-labelledby="staticBackdropLabel"
              style={{
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "block",
              }}
            >
              <div className="modal-dialog modal-xl">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      Appointments for Dr. {viewAppointmentDoctor.fullName}
                    </h5>

                  </div>
                  <div className="modal-body">
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Patient Name</th>
                            <th>Doctor Name</th>
                            <th>Date</th>
                            <th>Start Time</th>
                            <th>End Time</th>
                            <th>Reason</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appointmentData && appointmentData.length > 0 ? (
                            appointmentData.map((item, index) => (
                              <tr key={index}>
                                <td>{item.patientName}</td>
                                <td>{item.doctorName}</td>
                                <td>
                                  {new Date(item.appointmentDate).toLocaleDateString("en-GB")}
                                </td>
                                <td>{item.startTime}</td>
                                <td>{item.endTime}</td>
                                <td>{item.reason}</td>
                                <td>
                                  <span className={`badge ${item.status === 'Confirmed' ? 'bg-success' :
                                    item.status === 'Completed' ? 'bg-primary' :
                                      item.status === 'Pending' ? 'bg-warning' : 'bg-secondary'
                                    }`}>
                                    {item.status}
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center text-muted">
                                No appointments found for this doctor
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="cancelText"
                      onClick={handleViewAppointmentClose}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
      <ToastContainer />

      {permissionModalOpen && (
        <div className="modal fade show" aria-labelledby="permissionModalLabel" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'block' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="permissionModalLabel">
                  Staff Permissions{selectedAccount?.fullName ? ` - ${selectedAccount.fullName}` : ''}
                </h5>
                <button type="button" className="btn-close" onClick={closePermissionModal}></button>
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Module</th>
                        <th style={{ width: 120 }}>View</th>
                        <th style={{ width: 120 }}>Edit</th>
                        <th style={{ width: 120 }}>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modulesList.map(m => (
                        <tr key={m.key}>
                          <td>{m.label}</td>
                          <td>
                            <input type="checkbox" className="form-check-input" checked={!!permissions[m.key]?.view} onChange={() => togglePermission(m.key, 'view')} />
                          </td>
                          <td>
                            <input type="checkbox" className="form-check-input" checked={!!permissions[m.key]?.edit} onChange={() => togglePermission(m.key, 'edit')} />
                          </td>
                          <td>
                            <input type="checkbox" className="form-check-input" checked={!!permissions[m.key]?.delete} onChange={() => togglePermission(m.key, 'delete')} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closePermissionModal}>Close</button>
                <button type="button" className="btn btn-primary" onClick={handleSavePermissions}>Update Role</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
